/**
 * ChatFeedScrollContext — AI-First Phase 4 (#370)
 *
 * Verbindet InsightBell (Consumer) und CommandDashboard (Provider) über
 * einen gemeinsamen Scroll-Handler für den Chat-Feed. Wenn ui_ai_first
 * aktiv ist, scrollt ein Klick auf die Bell zur neuesten ungelesenen
 * Smart Card anstatt ein Popover zu öffnen.
 *
 * Mechanismus:
 *   1. CommandDashboard ruft `registerScroller(fn)` mit seiner
 *      scrollToCard-Implementierung.
 *   2. InsightBell ruft `scrollToSuggestion(id)` auf — der Kontext
 *      leitet den Aufruf an den registrierten Scroller weiter.
 *
 * Bewusst NICHT in AiCompanionContext integriert: Scroll-Logik ist reine
 * UI-Koordination; sie hat nichts mit Daten-Fetching zu tun.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useRef,
} from 'react';

// ── Kontext-Shape ─────────────────────────────────────────────────────────────

interface ChatFeedScrollContextValue {
  /** Scrollt im Chat-Feed zur Card mit der gegebenen Suggestion-ID. */
  scrollToSuggestion: (id: string) => void;
  /**
   * Registriert den Scroll-Handler. Wird von CommandDashboard nach dem
   * Mount aufgerufen. Kann mehrfach aufgerufen werden — die neueste
   * Registrierung gewinnt.
   */
  registerScroller: (fn: (id: string) => void) => void;
}

export const ChatFeedScrollContext = createContext<ChatFeedScrollContextValue>({
  scrollToSuggestion: () => {},
  registerScroller: () => {},
});

// ── Provider ──────────────────────────────────────────────────────────────────

export const ChatFeedScrollProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const scrollerRef = useRef<((id: string) => void) | null>(null);

  const registerScroller = useCallback((fn: (id: string) => void) => {
    scrollerRef.current = fn;
  }, []);

  const scrollToSuggestion = useCallback((id: string) => {
    scrollerRef.current?.(id);
  }, []);

  return (
    <ChatFeedScrollContext.Provider value={{ scrollToSuggestion, registerScroller }}>
      {children}
    </ChatFeedScrollContext.Provider>
  );
};

// ── Hook ─────────────────────────────────────────────────────────────────────

export function useChatFeedScroll(): ChatFeedScrollContextValue {
  return useContext(ChatFeedScrollContext);
}
